import { AxiosError } from 'axios';
import { UnknownErrorAndWeCouldNot } from '@/helpers/errors/UnknownErrorAndWeCouldNot';
import { UseMutationOptions } from '@tanstack/react-query';
import { CustomApiError } from '@/types/error';

const HTTP_SERVER_ERROR_STATUS = 500;

export const getResponseDetail = (data: unknown) => {
  if (typeof data !== 'object' || data === null || !('detail' in data)) {
    return undefined;
  }

  const { detail } = data as { detail?: unknown };

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const { msg } = (detail[0] ?? {}) as { msg?: unknown };

    return typeof msg === 'string' ? msg : undefined;
  }

  return undefined;
};

export const handleCatch = (reason: unknown, customErrorText: string) => {
  if (reason instanceof AxiosError && reason.response) {
    const errorMessage =
      getResponseDetail(reason.response.data) ??
      UnknownErrorAndWeCouldNot.getErrorMessage(customErrorText);
    throw new Error(errorMessage);
  } else {
    return Promise.reject(new UnknownErrorAndWeCouldNot(customErrorText));
  }
};

export const handleCatchWithCustomError = (reason: unknown, customErrorText: string) => {
  if (reason instanceof AxiosError && reason.response) {
    const errorMessage =
      getResponseDetail(reason.response.data) ??
      UnknownErrorAndWeCouldNot.getErrorMessage(customErrorText);
    const customError = new Error(errorMessage) as CustomApiError;
    customError.status = reason.response.status;
    throw customError;
  } else {
    return Promise.reject(new UnknownErrorAndWeCouldNot(customErrorText));
  }
};

export const handleCatchBasedOnStatus = (
  error: AxiosError,
  status: number,
  customErrorText: string,
) => {
  if (error.response?.status === status) {
    const { detail } = error.response?.data as Record<string, string>;
    throw new Error(detail);
  }
  throw new Error(customErrorText);
};

export const errorAndSuccessMethods = <TData, TError, TVariables, TOnMutateResult>(
  options: Pick<
    UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
    'onSuccess' | 'onError'
  >,
) => ({
  onSuccess: async (
    ...params: Parameters<
      NonNullable<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>['onSuccess']>
    >
  ) => {
    options?.onSuccess?.(...params);
  },
  onError: async (
    ...params: Parameters<
      NonNullable<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>['onError']>
    >
  ) => {
    options?.onError?.(...params);
  },
});

export const retryXTimesOnServerError = (
  failureCount: number,
  error: Error,
  retryNumber: number,
) => {
  const { status } = error as CustomApiError;

  if (status && status < HTTP_SERVER_ERROR_STATUS) {
    return false;
  }

  return failureCount < retryNumber;
};

export const retryXTimesWithStatusException = (
  failureCount: number,
  error: Error,
  statusException: number,
  retryNumber: number,
) => {
  if (error.message.includes(statusException.toString())) {
    return false;
  }
  return failureCount < retryNumber;
};
